import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import InvoicesPage from "./pages/InvoicesPage";
import CreateInvoicePage from "./pages/CreateInvoicePage";
import EditInvoicePage from "./pages/EditInvoicePage";
import InvoiceDetailPage from "./pages/InvoiceDetailPage";
import QuotesPage from "./pages/QuotesPage";
import CreateQuotePage from "./pages/CreateQuotePage";
import ClientsPage from "./pages/ClientsPage";
import ClientDetailPage from "./pages/ClientDetailPage";
import ProductsPage from "./pages/ProductsPage";
import ProjectsPage from "./pages/ProjectsPage";
import ProjectDetailPage from "./pages/ProjectDetailPage";
import SettingsPage from "./pages/SettingsPage";
import AdminPage from "./pages/AdminPage";
import FAQPage from "./pages/FAQPage";
import PricingPage from "./pages/PricingPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import LandingPage from "./pages/LandingPage";
import ExpensesPage from "./pages/ExpensesPage";
import RevenuesPage from "./pages/RevenuesPage";
import ProjectionsPage from "./pages/ProjectionsPage";
import ManualInvoicePage from "./pages/ManualInvoicePage";
import FiscalPage from "./pages/FiscalPage";
import LegalPage from "./pages/LegalPage";
import PortalPage from "./pages/PortalPage";
import PrivacyPage from "./pages/PrivacyPage";
import RemindersPage from "./pages/RemindersPage";
import RecurringInvoicesPage from "./pages/RecurringInvoicesPage";
import ShopifyPage from "./pages/ShopifyPage";
import TeamPage from "./pages/TeamPage";
import EmployeesPage from "./pages/EmployeesPage";
import PayslipsPage from "./pages/PayslipsPage";
import AbsencesPage from "./pages/AbsencesPage";
import AccountingEntriesPage from "./pages/AccountingEntriesPage";
import HRDashboardPage from "./pages/HRDashboardPage";
import SupportPage from "./pages/SupportPage";
import AdminTicketsPage from "./pages/AdminTicketsPage";
import AuditLogPage from "./pages/AuditLogPage";
import Layout from "./components/Layout";
import PlanGate from "./components/PlanGate";
import "./App.css";

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-[#0a0a0b]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-[#00F0FF]"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-[#0a0a0b]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-[#00F0FF]"></div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

const HomeRedirect = () => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-[#0a0a0b]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-[#00F0FF]"></div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <LandingPage />;
};

function App() {
  return (
  <ThemeProvider>
  <LanguageProvider>
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors closeButton />
        <Routes>
          {/* Landing page for visitors */}
          <Route path="/" element={<HomeRedirect />} />
          
          {/* Public pages */}
          <Route path="/legal" element={<LegalPage />} />
          <Route path="/portal/:token" element={<PortalPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          
          <Route path="/login" element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          } />
          <Route path="/register" element={
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          } />
          <Route path="/app" element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }>
            <Route index element={<Navigate to="/app/dashboard" replace />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="invoices" element={<InvoicesPage />} />
            <Route path="invoices/new" element={<CreateInvoicePage />} />
            <Route path="invoices/manual" element={<ManualInvoicePage />} />
            <Route path="invoices/:id" element={<InvoiceDetailPage />} />
            <Route path="invoices/:id/edit" element={<EditInvoicePage />} />
            <Route path="recurring" element={<PlanGate module="recurring"><RecurringInvoicesPage /></PlanGate>} />
            <Route path="shopify" element={<PlanGate module="shopify"><ShopifyPage /></PlanGate>} />
            <Route path="quotes" element={<QuotesPage />} />
            <Route path="quotes/new" element={<CreateQuotePage />} />
            <Route path="clients" element={<ClientsPage />} />
            <Route path="clients/:id" element={<ClientDetailPage />} />
            <Route path="products" element={<ProductsPage />} />
            <Route path="projects" element={<ProjectsPage />} />
            <Route path="projects/:projectId" element={<ProjectDetailPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="expenses" element={<ExpensesPage />} />
            <Route path="revenues" element={<RevenuesPage />} />
            <Route path="projections" element={<ProjectionsPage />} />
            <Route path="fiscal" element={<FiscalPage />} />
            <Route path="reminders" element={<RemindersPage />} />
            <Route path="team" element={<PlanGate module="team"><TeamPage /></PlanGate>} />
            <Route path="employees" element={<PlanGate module="employees"><EmployeesPage /></PlanGate>} />
            <Route path="payslips" element={<PlanGate module="payslips"><PayslipsPage /></PlanGate>} />
            <Route path="absences" element={<PlanGate module="absences"><AbsencesPage /></PlanGate>} />
            <Route path="accounting" element={<PlanGate module="accounting"><AccountingEntriesPage /></PlanGate>} />
            <Route path="hr-dashboard" element={<PlanGate module="hr-dashboard"><HRDashboardPage /></PlanGate>} />
            <Route path="admin" element={<AdminPage />} />
            <Route path="admin/tickets" element={<AdminTicketsPage />} />
            <Route path="audit-log" element={<AuditLogPage />} />
            <Route path="support" element={<SupportPage />} />
            <Route path="faq" element={<FAQPage />} />
          </Route>
          
          {/* Legacy routes redirect */}
          <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />
          <Route path="/invoices/*" element={<Navigate to="/app/invoices" replace />} />
          <Route path="/quotes/*" element={<Navigate to="/app/quotes" replace />} />
          <Route path="/clients/*" element={<Navigate to="/app/clients" replace />} />
          <Route path="/products" element={<Navigate to="/app/products" replace />} />
          <Route path="/settings" element={<Navigate to="/app/settings" replace />} />
          <Route path="/admin" element={<Navigate to="/app/admin" replace />} />
          <Route path="/faq" element={<Navigate to="/app/faq" replace />} />
          
          {/* Public pages */}
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/payment/success" element={
            <ProtectedRoute>
              <PaymentSuccessPage />
            </ProtectedRoute>
          } />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  </LanguageProvider>
  </ThemeProvider>
  );
}

export default App;
